// Report analytics — pure RPC layer (no React, no hooks, no JSX).

import { ANALYTICS_SERVICE_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  IAuditDrilldownBody,
  IAuditDrilldownResponse,
  IAuditImpactAreasResponse,
  IAuditMetrics,
  IAuditUrgenciesResponse,
  ICertByStandardMetrics,
  ICertDrilldownBody,
  ICertDrilldownResponse,
  ICertMetrics,
  IExportPayload,
  ITracingDepthCompletion,
  ITracingMetrics,
  ITracingPoDocumentsResponse,
} from './types';

const BASE = ANALYTICS_SERVICE_URL;

// ── Tracing Operations ────────────────────────────────────────────────────────

export function fetchTracingMetrics(
  companyId: string,
  filter: string,
): Promise<ITracingMetrics> {
  return apiFetch<ITracingMetrics>(
    `/v1/tracing-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchTracingDepthCompletion(
  companyId: string,
  filter: string,
): Promise<ITracingDepthCompletion[]> {
  return apiFetch<ITracingDepthCompletion[]>(
    `/v1/tracing-documents/completion/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchTracingPoDocuments(
  companyId: string,
  qs: string,
): Promise<ITracingPoDocumentsResponse> {
  return apiFetch<ITracingPoDocumentsResponse>(
    `/v1/tracing-documents/${companyId}?${qs}`,
    {},
    BASE,
  );
}

export function exportTracingMetrics(body: IExportPayload): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/tracing-metrics/export',
    { method: 'POST', body: JSON.stringify(body) },
    BASE,
  );
}

// ── Certificate Compliance ────────────────────────────────────────────────────

export function fetchCertMetrics(
  companyId: string,
  filter: string,
): Promise<ICertMetrics> {
  return apiFetch<ICertMetrics>(
    `/v1/certification-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchCertByStandardMetrics(
  companyId: string,
  filter: string,
): Promise<ICertByStandardMetrics> {
  return apiFetch<ICertByStandardMetrics>(
    `/v1/certification-metrics/by-standard/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchCertDrilldown(
  body: ICertDrilldownBody,
): Promise<ICertDrilldownResponse> {
  return apiFetch<ICertDrilldownResponse>(
    '/dashboard-analytics/certification-drilldown/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export function exportCertMetrics(filter: string): Promise<unknown> {
  return apiFetch<unknown>(
    '/v1/certification-metrics/export',
    { method: 'POST', body: JSON.stringify({ filter }) },
    BASE,
  );
}

// ── Audit Compliance ─────────────────────────────────────────────────────────

export function fetchAuditMetrics(
  companyId: string,
  filter: string,
): Promise<IAuditMetrics> {
  return apiFetch<IAuditMetrics>(
    `/v1/audit-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchAuditUrgencies(
  companyId: string,
  filter: string,
): Promise<IAuditUrgenciesResponse> {
  return apiFetch<IAuditUrgenciesResponse>(
    `/v1/audit-metrics/urgencies/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BASE,
  );
}

export function fetchAuditImpactAreas(
  companyId: string,
  qs: string,
): Promise<IAuditImpactAreasResponse> {
  return apiFetch<IAuditImpactAreasResponse>(
    `/v1/audit-metrics/impact-areas/${companyId}?${qs}`,
    {},
    BASE,
  );
}

export function fetchAuditDrilldown(
  body: IAuditDrilldownBody,
): Promise<IAuditDrilldownResponse> {
  return apiFetch<IAuditDrilldownResponse>(
    '/dashboard-analytics/audits-metrics/drill-down/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}
